import { IEmarsys } from "../Product/Interfaces/IEmarsys";

declare const ScarabQueue: IEmarsys;

interface IEmarsysRecommendation {
    id: string;
}

interface IEmarsysResponse {
    page: {
        products: IEmarsysRecommendation[];
    };
}
export class ScrollProducts {
    private containerParent = document.getElementById("footer-products");
    private recommendationIds: string[] = []; // Pos colour / style ids received from emarsys for the recommended products scroll view.
    private theLookIds: string[] = []; // Pos colour / style ids received from emarsys for replacing 'The Look' scroll view.
    private callbacksWaiting: number; // Tracks the data requested from emarsys in case we ask for multiple sets of recommendations.
    private theLookEmarsysHtml: string; // Cached 'recommended' HTML from the list of recommended products suggested by emarsys.

    private readonly desktopTheLookContainerId = "TheLookResults";
    private readonly mobileTheLookContainerId = "TheLookMobile";
    private readonly desktopTheLookMagicScrollId = "the-look-ms";
    private readonly mobileTheLookMagicScrollId = "the-look-ms-mobile";

    public static Init(): ScrollProducts {
        return new ScrollProducts();
    }

    constructor() {
        if (this.containerParent) { // The footer container does not exist on some pages.
            this.requestRecommendations();
        }
    }

    private requestRecommendations(): void {
        let logic = this.containerParent.dataset["logic"];
        let loadRecommended = this.containerParent.dataset["loadRecommended"];

        if (!logic) {
            logic = "PERSONAL"; // Default for pages that do not define a parameter for the recommendations.
        }

        this.callbacksWaiting = 1;
        ScarabQueue.push(["recommend", {
            logic: logic,
            limit: 10,
            containerId: "recommendations",
            success: (sc: IEmarsysResponse) => { this.recommendationsReceived(sc); }
        }]);
        if (loadRecommended) {
            let containerId;

            if (document.getElementById(this.desktopTheLookContainerId)) {
                containerId = this.desktopTheLookContainerId;
            }
            else if (document.getElementById(this.mobileTheLookContainerId)) {
                containerId = this.mobileTheLookContainerId;
            }
            // We are being called from the product page so request the additional data for The Look in case we need to use it.
            this.callbacksWaiting = 2;
            ScarabQueue.push(["recommend", {
                logic: "PERSONAL",
                limit: 10,
                containerId: containerId,
                success: (sc: IEmarsysResponse) => { this.theLookRecommendationsReceived(sc); }
            }]);
        }
        ScarabQueue.push(['go']);
    }

    /*
     * Checks if there is any data left to come back from the emarsys SDK.
     *
     * If all data has come back then packages up a request to our server
     * for the full product details, passing the ids received from emarsys.
     * 
     * The data is sent back in one go but we get one or two different callbacks
     * from the JS SDK depending on if we asked for product The Look replacement
     * data as well.
    */
    private processEmarsysCallback() {
        if (--this.callbacksWaiting <= 0) {
            // Bundle up both lots of ids with the exclude style id.
            let exclude = this.containerParent.dataset["exclude"];
            let styleId = 0;
            if (exclude) {
                styleId = parseInt(exclude);
            }

            let localeUrlPrefix = document.body.dataset.localeUrlPrefix;
            let recommendedIdsParam = this.recommendationIds.join("&");
            let theLookIdsParam = this.theLookIds.join("&");
            let styleIdParam = `styleId=${styleId}`;
            let qsParams = this.joinWithDelimiter("&", recommendedIdsParam, theLookIdsParam, styleIdParam);
            let url = `${localeUrlPrefix}/ws/scroll-products?${qsParams}`;

            window.HttpRequest.Get(url, "",
                (data: XMLHttpRequest) => { this.onScrollProductsViewReady(data); });
        }
    }

    private joinWithDelimiter(delimiter: string, ...args: string[]): string {
        return args
            .filter(str => str !== undefined && str !== null && str !== '')
            .join(delimiter);
    }

    private theLookRecommendationsReceived(SC: IEmarsysResponse) {
        this.theLookIds = this.extractProductIds(SC.page.products, "theLookIds");
        this.processEmarsysCallback();
    }

    private recommendationsReceived(SC: IEmarsysResponse): void {
        this.recommendationIds = this.extractProductIds(SC.page.products, "recommendedIds");
        this.processEmarsysCallback();
    }

    private extractProductIds(products: IEmarsysRecommendation[], paramName: string): string[] {
        let ids: string[] = [];

        for (let i = 0, l = products.length; i < l; i++) {
            let product = products[i];
            ids.push(paramName + "=" + product.id);
        }

        return ids;
    }

    /*
     * Server has processed the lists of emarsys product codes and sent us enough details
     * to generate scrollable product views.
    */
    private onScrollProductsViewReady(response: XMLHttpRequest): void {
        // Create an element to load the serverside HTML into.
        let tempParentDiv = document.createElement("div");
        tempParentDiv.innerHTML = response.response;

        this.copyRecsAndRecentToUI(tempParentDiv);
        this.copyTheLookToUI(tempParentDiv, this.desktopTheLookContainerId, this.desktopTheLookMagicScrollId);
        this.copyTheLookToUI(tempParentDiv, this.mobileTheLookContainerId, this.mobileTheLookMagicScrollId);

        tempParentDiv.remove();
        tempParentDiv = null;

        this.wireUpRecentlyViewedDeleting();
        this.createScroll("recommended-products");
        this.createScroll("recent-products");
    }

    private copyRecsAndRecentToUI(tempParentDiv: HTMLDivElement): void {
        // Check if we got any data returned in the product containers.
        let tempRecommendations = tempParentDiv.querySelector("#recommendations");
        let tempRecent = tempParentDiv.querySelector("#recently-viewed");

        if (!tempRecent && !tempRecommendations) {
            // No recommendations or recently viewed.
            this.containerParent.remove();
            this.containerParent = null;
            return;
        }

        let liveRecent = document.getElementById("recently-viewed");
        let liveRecommendations = document.getElementById("recommendations");

        if (tempRecent && tempRecommendations) {
            // Both of the containers are visible so the col-wide-6 classes are left in place.
            liveRecent.innerHTML = tempRecent.innerHTML;
            liveRecommendations.innerHTML = tempRecommendations.innerHTML;
        }
        else {
            // Only one of the containers returned data, load it, adjust the width and hide the other one.
            if (tempRecent) {
                liveRecent.innerHTML = tempRecent.innerHTML;
                liveRecent.parentElement.classList.remove("col-wide-6");
                liveRecent.parentElement.classList.add("col-wide-12");

                if (liveRecommendations) {
                    liveRecommendations.parentElement.remove();
                }
                liveRecommendations = null;
            }
            else {
                liveRecommendations.innerHTML = tempRecommendations.innerHTML;
                liveRecommendations.parentElement.classList.remove("col-wide-6");
                liveRecommendations.parentElement.classList.add("col-wide-12");

                if (liveRecent) {
                    liveRecent.parentElement.remove();
                }
                liveRecent = null;
            }
        }

        // Just in case of memory leaks.
        if (tempRecent) {
            tempRecent.remove();
            tempRecent = null;
        }
        if (tempRecommendations) {
            tempRecommendations.remove();
            tempRecommendations = null;
        }
    }

    private hasNonTextChildNodes(node: Element): boolean {
        for (const element of node.childNodes) {
            if (element.nodeType !== Node.TEXT_NODE) {
                return true;
            }
        }
        return false;
    }

    private copyTheLookToUI(tempParentDiv: HTMLDivElement, containerId: string, magicScrollId: string): void {

        if (!this.containerParent) { // The footer container does not exist on some pages.
            return;
        }

        // Ensure the container and scroll elements exist, might not for mobile in desktop view and vice-versa.
        let c = document.getElementById(containerId);
        let s = document.getElementById(magicScrollId);
        if (!c || !s) {
            return;
        }

        // Check if we got any data returned in the product containers.
        // These ids are embedded in the HTML that came back and are based on the desktop view which is why we use the same selectors.
        let tempTheLook = tempParentDiv.querySelector("#" + this.desktopTheLookContainerId);
        let tempTheLookProductContainer = tempParentDiv.querySelector("#" + this.desktopTheLookMagicScrollId)

        if (!tempTheLook || !this.hasNonTextChildNodes(tempTheLookProductContainer)) {
            return;
        }

        this.theLookEmarsysHtml = tempTheLookProductContainer.innerHTML;

        let replaceTheLook = this.containerParent.dataset["replaceTheLook"];

        if (replaceTheLook) {
            // We are being called from the product page and it has < 2 The Look items to show on page load.
            this.populateTheLook(tempTheLookProductContainer.innerHTML, containerId, magicScrollId);
        }
    }

    /* When a request for The Look data for a colour fails to produce any recommendations this
     * reverts the list of recommendations to the one that emarsys sent us.
     */
    public restoreTheLookCache(containerId: string, magicScrollId: string): void {
        this.populateTheLook(this.theLookEmarsysHtml, containerId, magicScrollId);
    }

    private populateTheLook(html: string, containerId: string, magicScrollId: string): void {
        let c = document.getElementById(containerId);
        let s = document.getElementById(magicScrollId);

        if (!c || !s) {
            return;
        }

        MagicScroll.stop(magicScrollId);

        s.innerHTML = html;
        s.classList.remove("hidden");
        c.classList.remove("hidden");

        if (MagicScroll.running(magicScrollId)) {
            MagicScroll.refresh(magicScrollId);
        }
        MagicScroll.start(magicScrollId);
    }

    private wireUpRecentlyViewedDeleting(): void {
        document.body.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;

            // Check if the user clicked on an element inside #recent-products .mcs-item .delete
            // Use .closest() so that if the click is on a child element (e.g., an icon),
            // it will still detect the parent with the .delete class
            const deleteLink = target.closest("#recent-products .mcs-item .delete");

            if (deleteLink) {
                e.preventDefault();
                this.onRecentlyViewedDeleteClicked(deleteLink as HTMLAnchorElement);
            }
        });
    }

    private onRecentlyViewedDeleteClicked(deleteLink: HTMLAnchorElement): void {
        let item = deleteLink.parentElement;
        let scrollItem = item.parentElement;
        let scrollIndex = scrollItem.dataset["item"];
        let index = parseInt(scrollIndex);

        if (!isNaN(index)) {
            MagicScroll.removeItem("recent-products", index);

            let styleIdText = item.dataset["styleid"];
            let styleId = parseInt(styleIdText);

            // Send delete back to server with style id which will send back an updated cookie.
            let jsonPost = JSON.stringify({ styleId });
            window.HttpRequest.PostJson('/ws/scroll-remove', jsonPost,
                () => { },
                () => { window.IssueLogger.log("Unable to delete recently reviews", "onRecentlyViewedDeleteClicked"); });
        }
    }

    private createScroll(elementId: string): void {
        let scroll = document.getElementById(elementId);

        if (scroll) { // Scroller element is not emitted if empty, so check before using.
            scroll.className = "MagicScroll";

            if (MagicScroll.running(elementId)) {
                MagicScroll.refresh(elementId);
            }
            MagicScroll.start(elementId);
        }
    }
}

document.addEventListener("DOMContentLoaded", () => { window.scrollProducts = ScrollProducts.Init(); });
